/**
 * Game API Service
 *
 * Following SOLID principles:
 * - Single Responsibility: Only handles API calls for Game component
 * - Open/Closed: Extendable through inheritance without modification
 * - Dependency Inversion: Depends on abstractions (httpService)
 */
import axios from 'axios';
import { HOST_URL, API_V2, API_V4 } from '@/config/api';

/**
 * Game API Service class
 * Encapsulates all API calls for the main Game component
 */
export class GameApiService {
  constructor(baseUrl = HOST_URL) {
    this.baseUrl = baseUrl;
    this.http = axios.create({ baseURL: baseUrl });
  }

  /**
   * Get member profile information
   * @param {string} lineId - LINE user ID
   * @returns {Promise<Object>} Member profile data
   */
  async getMemberInfo(lineId) {
    const response = await this.http.get(`${API_V2}/member_infos/${lineId}`);
    return response.data;
  }

  /**
   * Get topic information for a member
   * @param {string} lineId - LINE user ID
   * @returns {Promise<Object>} Topic and profile data
   */
  async getTopicInfo(lineId) {
    const response = await this.http.get(`${API_V2}/members/${lineId}`);
    return response.data;
  }

  /**
   * Get group story ship information
   * @param {string} groupId - Group ID
   * @param {string} memberLineId - Member LINE ID
   * @returns {Promise<Object>} Group story ship data including story_id
   */
  async getGroupStoryShip(groupId, memberLineId) {
    const effectiveGroupId = groupId || memberLineId;
    const response = await this.http.get(
      `${API_V4}/groups/${effectiveGroupId}/group_story_ships/${memberLineId}`
    );
    return response.data;
  }

  /**
   * Update group story ship with found item
   * @param {string} groupId - Group ID
   * @param {string} memberLineId - Member LINE ID
   * @param {string} itemType - Type of item found
   * @param {string} itemName - Name of item found
   * @param {Object} options - Additional options
   * @returns {Promise<void>}
   */
  async updateGroupStoryShip(groupId, memberLineId, itemType, itemName, options = {}) {
    const effectiveGroupId = groupId || memberLineId;
    await this.http.put(
      `${API_V4}/groups/${effectiveGroupId}/group_story_ships/${memberLineId}`,
      { item_type: itemType, item_name: itemName, ...options }
    );
  }
}

// Default instance for convenience
export const gameApiService = new GameApiService();

/**
 * Composable hook for using Game API in Vue components
 * @param {Object} options - Configuration options
 * @returns {Object} API methods
 */
export function useGameApi(options = {}) {
  const service = options.service || gameApiService;

  return {
    /**
     * Fetch member profile and return display name
     * @param {string} lineId - LINE user ID
     * @returns {Promise<Object>} Profile with displayName
     */
    async fetchMemberProfile(lineId) {
      const profile = await service.getMemberInfo(lineId);
      return profile;
    },

    /**
     * Fetch topic info and return topic and profile
     * @param {string} lineId - LINE user ID
     * @returns {Promise<{topic: Object, profile: Object}>}
     */
    async fetchTopicInfo(lineId) {
      const data = await service.getTopicInfo(lineId);
      return {
        topic: data.topic,
        profile: data.profile
      };
    },

    /**
     * Fetch story ID from group story ship
     * @param {string} groupId - Group ID
     * @param {string} lineId - LINE user ID
     * @returns {Promise<string>} Story ID
     */
    async fetchStoryId(groupId, lineId) {
      const data = await service.getGroupStoryShip(groupId, lineId);
      return data.ship.story_id;
    },

    /**
     * Create curried update function for group story ship
     * @param {string} groupId - Group ID
     * @param {string} lineId - LINE user ID
     * @returns {Function} Update function
     */
    createUpdateGroupStoryShip(groupId, lineId) {
      return async (itemType, itemName, options = {}) => {
        await service.updateGroupStoryShip(groupId, lineId, itemType, itemName, options);
      };
    }
  };
}

export default gameApiService;
